import matches from 'lodash/matches';
import { Subject, Subscription } from 'rxjs';
import { IMessageEvent, w3cwebsocket as W3CWebsocket } from 'websocket';
import { isEqual } from 'lodash';
import {
  WebsocketCommandType,
  WebsocketEvent,
  WebsocketEventFilter,
} from './types/websocketTypes';

const RECONNECTION_RETRY_DELAY_IN_MS = 10 * 1000;

// When using the service, first call the connect function at application initialization,
// so this will make sure the channel is open for further messages during application lifetime
export default class WebsocketService {
  // The Websocket client
  private static ws: W3CWebsocket;

  // List of events we are interested in, along with their callbacks
  // This list is maintained to handle scenarios where disconnections happens (websocket dies for any reason) and we need to re-subscribe to the events
  private static subscriptions: {
    websocketEventFilter: WebsocketEventFilter;
    rxjsSubscription: Subscription;
    callback: (websocketEvent: WebsocketEvent) => void;
  }[] = [];

  // The internal pub/sub mechanism in the client using rxjs so messages coming from websocket are published to their subscribers
  private static subject: Subject<WebsocketEvent>;

  // Signals if we actually want to shutdown and avoid reconnect
  private static isDisconnectAndResetCalled = false;

  // Promise that guarantees, before subscription, we have actually opened the websocket
  public static isConnected: Promise<boolean>;

  // Call this function at the earliest in the client as possible, to establish the websocket connection
  static connect(websocketsUrl: string) {
    WebsocketService.isDisconnectAndResetCalled = false;
    WebsocketService.isConnected = new Promise(resolve => {
      // rxjs "pub/sub"
      WebsocketService.subject = new Subject();

      // Encapsulate the websocket variable into a function - so on reconnect everything works smoothly
      function wsConnect() {
        WebsocketService.ws = new W3CWebsocket(websocketsUrl);
        WebsocketService.ws.onopen = function onopen() {
          // This makes sure to re-subscribe to all events we subscribed before, upon reconnect
          WebsocketService.subscriptions.forEach(({ websocketEventFilter }) => {
            if (WebsocketService.ws.readyState === WebsocketService.ws.OPEN) {
              WebsocketService.ws.send(
                JSON.stringify({
                  command: WebsocketCommandType.SUBSCRIBE,
                  websocketEventFilter,
                }),
              );
            }
          });
          resolve(true);
        };
        WebsocketService.ws.onmessage = function onmesssage(
          message: IMessageEvent,
        ) {
          WebsocketService.subject.next(JSON.parse(message.data.toString()));
        };

        WebsocketService.ws.onclose = function onclose() {
          // We were directly asked to shutdown, so do nothing on close.
          if (WebsocketService.isDisconnectAndResetCalled) {
            return;
          }

          // Socket was closed (server down or network issues). Reconnect will be attempted in 10 seconds
          setTimeout(function timeout() {
            wsConnect();
          }, RECONNECTION_RETRY_DELAY_IN_MS);
        };

        WebsocketService.ws.onerror = function onerror() {
          // Something went wrong, so we close the socket
          WebsocketService.ws.close();
        };
      }
      wsConnect();
    });
  }

  // Call this to tear down all connections, free subscription resources and make it ready to go again (reset)
  static disconnectAndReset() {
    WebsocketService.isDisconnectAndResetCalled = true;
    WebsocketService.ws.close();
    WebsocketService.subscriptions.forEach(subscription =>
      subscription.rxjsSubscription.unsubscribe(),
    );
    WebsocketService.subscriptions = [];
  }

  // Listen to a certain event type and assign a callback
  static async subscribe<T extends WebsocketEventFilter>(
    websocketEventFilter: T,
    callback: (websocketEvent: WebsocketEvent) => void,
  ) {
    const websocketEventFilterAlreadyRegistered = WebsocketService.subscriptions.find(
      subscription =>
        isEqual(subscription.websocketEventFilter, websocketEventFilter),
    );
    // Avoid subscribing twice
    if (websocketEventFilterAlreadyRegistered) return;

    // Add a subscription so when a Websocket event comes, we fire the targeted callback
    const rxjsSubscription = WebsocketService.subject.subscribe({
      next(websocketEvent) {
        if (matches(websocketEventFilter)(websocketEvent)) {
          callback(websocketEvent);
        }
      },
    });

    // Make sure to wait we have finished the connection phase, before sending a message, this is because
    // the connection and sending of message might occur in the same javascript event loop if we do not wait
    await WebsocketService.isConnected;

    if (WebsocketService.ws.readyState === WebsocketService.ws.OPEN) {
      WebsocketService.ws.send(
        JSON.stringify({
          command: WebsocketCommandType.SUBSCRIBE,
          websocketEventFilter,
        }),
      );
    }

    // Add the new websocketEventFilter to the local subscription lists so we can detect duplicates and also re-subscribe
    // on disconnections
    WebsocketService.subscriptions.push({
      websocketEventFilter,
      rxjsSubscription,
      callback,
    });
  }

  static unsubscribe<T extends WebsocketEventFilter>(websocketEventFilter: T) {
    WebsocketService.subscriptions = WebsocketService.subscriptions.filter(
      subscription => {
        if (matches(websocketEventFilter)(subscription.websocketEventFilter)) {
          if (WebsocketService.ws.readyState === WebsocketService.ws.OPEN) {
            WebsocketService.ws.send(
              JSON.stringify({
                command: WebsocketCommandType.UNSUBSCRIBE,
                websocketEventFilter,
              }),
            );
          }
          // Also remove subscription form the rjxs Subject
          subscription.rxjsSubscription.unsubscribe();
          return false;
        }
        return true;
      },
    );
  }
}
